import React, { memo, useCallback } from 'react'
import { Button } from '@refera/ui-web'
import { Trash } from '@refera/ui-icons'

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import {
  MATERIAL_TABLE_DEFAULT_ITEM,
  MATERIAL_TABLE_COLUMNS_WITH_ACTION,
} from '_/views/budget-view-v2/utils/constants'
import { useDialog } from '_/hooks/use-dialog'
import IconButton from '_/components/svg/icon-button'
import { FormHelperText, Grid, Tooltip } from '@mui/material'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'

import useStyles from './styles'
import { useToast } from '_/hooks/use-toast'
import { findMaterialsErrorMessage } from '_/views/budget-view-v2/utils/functions'
import { TextInput } from '_/views/budget-view-v2/components/Inputs/TextInput'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'

const RenderTableField = memo(function RenderTableField({ rowIndex, column, showLabel, onRemove }) {
  const styles = useStyles()
  const { addProblemReadOnly } = useBudgetForm()
  const {
    formState: { errors, dirtyFields },
  } = useFormContext()
  const { key, type, defaultValue, placeholder, prefix = '', style, readOnly } = column
  const label = showLabel ? column.label : ''
  const rowValues = useWatch({ name: `materials[${rowIndex}]` })
  const total = key === 'total' && rowValues?.unitPrice * rowValues?.quantity
  const errorMessage = errors?.materials?.[rowIndex]?.[key]?.message

  const name = `materials[${rowIndex}].${key}`
  const rowDirtyFields = dirtyFields?.materials?.[rowIndex]
  const shouldShowTotal = rowDirtyFields?.unitPrice || rowDirtyFields?.quantity

  return (
    <Grid item sx={style}>
      {type === 'text' && (
        <TextInput
          name={name}
          label={label}
          error={errorMessage}
          placeholder={placeholder}
          defaultValue={rowValues?.[key] || defaultValue}
          disabled={addProblemReadOnly}
          rows={1}
        />
      )}
      {type === 'currency' && (
        <NumericInput
          label={label}
          name={name}
          placeholder={placeholder}
          prefix={prefix}
          defaultValue={rowValues?.[key] || defaultValue}
          disabled={readOnly || addProblemReadOnly}
          hasError={errorMessage}
          {...(key === 'total' ? { value: shouldShowTotal ? total : rowValues?.[key] } : {})}
        />
      )}

      {type === 'number' && (
        <div
          className={styles.numberInput}
          style={{
            marginTop: showLabel ? '18px' : '0px',
          }}
        >
          {rowIndex + 1}
        </div>
      )}

      {type === 'remove' && !addProblemReadOnly && (
        <Tooltip
          title="Excluir problema"
          placement="top"
          className={styles.removeButton}
          style={{ marginTop: showLabel ? '18px' : '-2px' }}
        >
          <IconButton variant="ghost" onClick={() => onRemove(rowIndex)}>
            <Trash color="red" fontSize={38} />
          </IconButton>
        </Tooltip>
      )}
    </Grid>
  )
})

const Columns = memo(function Columns({ rowIndex, onRemove }) {
  return MATERIAL_TABLE_COLUMNS_WITH_ACTION.map(column => {
    const { key } = column
    const showLabel = rowIndex === 0
    return (
      <RenderTableField
        key={key}
        rowIndex={rowIndex}
        column={column}
        showLabel={showLabel}
        onRemove={onRemove}
      />
    )
  })
})

const MaterialRow = memo(function MaterialRow({ index, onRemove }) {
  const styles = useStyles()
  const {
    formState: { errors },
  } = useFormContext()
  const errorMessage = findMaterialsErrorMessage(errors, index)

  return (
    <Grid flexDirection="column" gap={2}>
      <Grid className={styles.row} container wrap="nowrap">
        <Columns rowIndex={index} onRemove={onRemove} />
      </Grid>
      {errorMessage ? (
        <FormHelperText className={styles.errorMessage} error={!!errorMessage}>
          {errorMessage}
        </FormHelperText>
      ) : null}
    </Grid>
  )
})

export function MaterialTable() {
  const styles = useStyles()
  const { closeDialog, showDialog } = useDialog()
  const { showToast } = useToast()
  const { control } = useFormContext()
  const { addProblemReadOnly } = useBudgetForm()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'materials',
  })

  const handleAppendItem = useCallback(() => {
    if (fields.length < 10) {
      append(MATERIAL_TABLE_DEFAULT_ITEM)
    } else {
      showToast({
        type: 'warning',
        message: 'Você só pode inserir até 10 materiais por serviço.',
      })
    }
  }, [append, MATERIAL_TABLE_DEFAULT_ITEM, fields, showToast])

  const handleRemove = useCallback(
    index => {
      showDialog({
        type: 'warning',
        subject: 'Remover material',
        description: 'Você tem certeza que deseja executar esta ação?',
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
        onCancel: closeDialog,
        onApprove: () => {
          remove(index)
          closeDialog()
        },
      })
    },
    [showDialog, closeDialog, remove]
  )

  return (
    <Grid container className={styles.container} flexWrap="nowrap">
      {fields?.map((item, index) => (
        <MaterialRow key={item.id} item={item} index={index} onRemove={handleRemove} />
      ))}
      {!addProblemReadOnly && (
        <Button
          variant="secondary"
          onClick={handleAppendItem}
          style={{ width: '160px', marginTop: '8px' }}
        >
          + Adicionar item
        </Button>
      )}
    </Grid>
  )
}
